import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import * as processors from './processors';
import {
  HostedFieldProcessorInterface,
  HostedFieldType,
  HostedFieldValue,
  PaymentProcessor,
} from '@zapier/payment/src/types';

const HostedFieldStyle = {
  // Custom font only works with Stripe for now as Braintree does not support it.
  // I've revived an issue on their side as technically they could support it but their
  // justification is vague:
  //   https://github.com/braintree/braintree-web/issues/75#issuecomment-441748583
  fontFamily: '"Open Sans", Helvetica, arial, sans-serif',
  fontSize: '14px',
  color: '#5f6c72',
  '::placeholder': {
    color: '#969ea2',
  },
};

class PaymentProcessorFailedInitializationError extends Error {
  title = "Something's not quite right.";
  description =
    "We're having some trouble connecting to our payment processor. Please wait a few minutes, refresh the page, and try again.";

  constructor(paymentProcessor: PaymentProcessor) {
    super(`Payment processor "${paymentProcessor}" failed to initialize`);
  }
}

const createPaymentProcessor = (
  paymentProcessor: PaymentProcessor,
  apiKey: string
): Promise<HostedFieldProcessorInterface> =>
  processors[paymentProcessor](apiKey);

export const usePaymentProcessor = (
  paymentProcessor: PaymentProcessor,
  apiKey: string,
  onChange: (name: HostedFieldType, value: HostedFieldValue) => void
): [
  HostedFieldProcessorInterface | undefined | null,
  PaymentProcessorFailedInitializationError | undefined | null,
  () => Promise<void>
] => {
  const [
    processor,
    setProcessor,
  ] = useState<HostedFieldProcessorInterface | null>(null);

  const [
    error,
    setError,
  ] = useState<PaymentProcessorFailedInitializationError | null>(null);
  /** Step 1. Initialize the payment processor, taking care to capture and show any errors resulting from this */
  useEffect(() => {
    const init = async () => {
      try {
        const initializedProcessor = await createPaymentProcessor(
          paymentProcessor,
          apiKey
        );
        setProcessor(initializedProcessor);
      } catch (err) {
        const e = new PaymentProcessorFailedInitializationError(
          paymentProcessor
        );

        Sentry.captureException(e, {
          tags: {
            package: 'payment',
          },
          extra: {
            parentError: JSON.stringify(err, Object.getOwnPropertyNames(err)),
          },
        });

        setError(e);
      }
    };

    init();
  }, [paymentProcessor, apiKey]);

  /** Step 2. Once the payment processor is initialized, create the hosted fields so they can receive input */
  const initializeHostedFields = useCallback(async () => {
    if (!processor) return;
    try {
      await processor.createFields(HostedFieldStyle, onChange);
    } catch (err) {
      // If the user switches to another page or to Paypal quickly
      // an error may be thrown here because the DOM elements won't exist anymore.
      // In that case we silently fail here.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processor]);

  useEffect(() => {
    if (!processor) return;

    initializeHostedFields();

    // eslint-disable-next-line consistent-return
    return () => {
      /** Step 3. Destroy the fields and un-initialize the processor whenever we unmount/change the processor */
      processor.destroyFields();
      setProcessor(null);
    };
  }, [initializeHostedFields, processor]);

  return [processor, error, initializeHostedFields];
};
